<template>
  <section>
    <Feature :title="$t('SUPERVISOR_MAINTAIN')" :iconClass="'fa-check-square'">
      <FormRow :fieldNameArr="[$t('DELTA_PN'), $t('VENDOR_CODE'), $t('APPLY_DATE'), '~', $t('STATUS')]">
        <Input v-model="deltaPn" search :enter-button="$t('MORE')" @on-search="moreDeltaPn" slot="a" />
        <Input v-model="vendorCode" search :enter-button="$t('MORE')" @on-search="moreVendorCode" slot="b" />
        <DatePicker v-model="fromRfqDate" type="date" format="yyyy-MM-dd" :placeholder="$t('VALID_FROM')" slot="c">
        </DatePicker>
        <DatePicker v-model="toRfqDate" type="date" format="yyyy-MM-dd" :placeholder="$t('VALID_TO')" slot="d">
        </DatePicker>
        <Select v-model="status" @on-change="changeStatus" slot="e">
          <Option v-for="(item, index) in statusList" :value="item.statusViewEnum" :key="index">{{ item.description }}
          </Option>
        </Select>
      </FormRow>
      <ButtonRow>
        <Button type="success" @click="search" slot="0">{{ $t('COMMON_TEXT_SEARCH') }}</Button>
        <Button type="info" @click="clear" slot="1">{{ $t('CLEAR') }}</Button>
      </ButtonRow>
    </Feature>
    <Feature :title="'Data'" :iconClass="'fa-pencil-square-o'">
      <FormRow v-if="status === 'PROCESSING'" :fieldNameArr="[$t('SELECT_ALL'), '']">
        <RadioGroup v-model="selectAllType" @on-change="changeSelectAll" slot="a">
          <Radio label="APPROVE">{{ $t("APPROVE") }}</Radio>
          <Radio label="REJECT">{{ $t("REJECT") }}</Radio>
        </RadioGroup>
        <Button type="primary" @click="cleanSelect" slot="b">{{ $t('CLEAR_SELECT') }}</Button>
      </FormRow>
      <FormRow :fieldNameArr="[ $t('LAYOUT') ]">
        <RadioGroup v-model="layoutType" slot="a">
          <Radio label="SIMPLE">{{ $t("SIMPLE") }}</Radio>
          <Radio label="DEFAULT">{{ $t("DEFAULT") }}</Radio>
        </RadioGroup>
      </FormRow>
      <Table v-show="layoutType === 'SIMPLE'" :columns="quotationColumnListForSimple" :data="quotationList" row-key="key">
        <template slot-scope="{ row, index }" slot="clear">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Button shape="circle" size="small" style="width: 80%" @click="clearAction(index)">{{ $t("CLEAR") }}</Button>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="approve">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Radio v-model="quotationList[index].selectApprove" style="display: inline-block; font-size: 14px;" @on-change="value => changeApprove(value, index)">{{ $t("APPROVE") }}</Radio>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="reject">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Radio v-model="quotationList[index].selectReject" style="display: inline-block; font-size: 14px;" @on-change="value => changeReject(value, index)">{{ $t("REJECT") }}</Radio>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="comments">
          <Icon v-if="row.resourceType !== 'AbnormalQuotationDto'" type="ios-create" size="28" class="text-delta-blue" @click="showCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="historyComments">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showHistoryCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="quotationFiles">
          <Icon v-if="row.isCoverQuote && row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="md-close-circle" size="28" class="text-delta-red" @click="showQuotationFilesModal(index)" />
          <Icon v-else-if="row.isCoverQuote" type="md-close-circle" size="28" class="text-delta-red" />
          <Icon v-else-if="row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="ios-cloud-download" size="28" class="text-delta-blue" @click="showQuotationFilesModal(index)"/>
        </template>
        <template slot-scope="{ row, index }" slot="referenceFile">
          <Icon :type="row.cpcToSupervisorAttachments && row.cpcToSupervisorAttachments.filter(attachement => !attachement.deleted).length > 0 ? 'ios-cloud-download' : ''" size="28" class="text-delta-blue" @click="showReferenceFileModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="plmFiles">
          <Icon type="md-download" size="28" class="text-delta-blue" @click="showPlmFilesModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="validFrom">
          <DatePicker :disabled="status !== 'PROCESSING'" :transfer="true" v-model="quotationList[index].validFrom" type="date" format="yyyy-MM-dd" placeholder="Select Date" @on-change="updateDate($event, index, 'validFrom')"></DatePicker>
        </template>
        <template slot-scope="{ row, index }" slot="validTo">
          <DatePicker :disabled="status !== 'PROCESSING'" :transfer="true" v-model="quotationList[index].validTo" type="date" format="yyyy-MM-dd" placeholder="Select Date" @on-change="updateDate($event, index, 'validTo')"></DatePicker>
        </template>
        <template slot-scope="{ row, index }" slot="highPrice">
          <div v-if="row.highPrice && row.quotationHighPrice">
            <Icon type="ios-warning" size="28" class="text-delta-red" />
            <span>{{ row.quotationHighPrice.priceDifference }}%</span>
            <br/>
            <span>{{ row.quotationHighPrice.orgCheck === 'C' ? 'Cross' : 'Single' }}</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="unitPriceAndCurrency">
          <div v-if="(row.abnormals && row.abnormals.quoteRedAlert) || row.quoteRedAlert">
            <Button type="error" style="width: 80%" shape="circle" size="small">{{ row.unitPrice }}</Button>
            <span>/{{ row.currency }}</span>
          </div>
          <div v-else>{{ row.unitPrice ? row.unitPrice : "" }}/{{ row.currency ? row.currency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="referencePriceData">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showReferencePriceDataModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="itemCategory">
          <span v-if="quotationList[index].itemCategory === 'STANDARD'">Standard</span>
          <span v-if="quotationList[index].itemCategory === 'SUBCONTRACTION'">Subcontraction</span>
        </template>
        <template slot-scope="{ row, index }" slot="averagePurchasePriceAndCurrency">
          <div>{{ row.averagePurchasePrice ? row.averagePurchasePrice : "" }}/{{ row.averagePurchaseCurrency ? row.averagePurchaseCurrency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="scalePrice">
          <Icon :type="row.quotationScalePriceList && row.quotationScalePriceList.length > 0 ? 'md-reorder' : ''" size="28" class="text-delta-blue" @click="showScalePriceModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="rawMaterialPriceAndUnit">
          <div>{{ row.rawMaterialPrice ? row.rawMaterialPrice : "" }}/{{ row.rawMaterialUnit ? row.rawMaterialUnit : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="remark">
          <Icon v-if="row.resourceType !== 'AbnormalQuotationDto'" type="ios-create" size="28" class="text-delta-blue" @click="showRemarkModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="ymgrRemark">
          <div>{{ row.ymgrRemark }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="log">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showLogModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="categoryAttributes">
          <Icon v-if="row.category && row.category !== 'OTHERS'" type="ios-paper" size="28" class="text-delta-blue" @click="showCategoryAttributesModal(index, row.category, row.formNo)" />
        </template>
        <template slot-scope="{ row, index }" slot="infoRecord">
          <div v-if="row.infoRecordFlag">
            <span>YES</span>
            <Icon type="ios-create" size="28" class="text-delta-blue" @click="showInfoRecordMaintainModal(index, row.uuid)" />
          </div>
          <div v-else>
            <span>NO</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="rfqType">
          <span>{{ row.rfqType.description }}</span>
        </template>
      </Table>
      <Table v-show="layoutType === 'DEFAULT'" :columns="quotationColumnList" :data="quotationList" row-key="key">
        <template slot-scope="{ row, index }" slot="action">
          <div v-if="row.resourceType === 'AbnormalQuotationDto'">
            <Button :type="row.status.statusViewEnum === 'CANCEL' ? 'error' : 'warning'" shape="circle" size="small" style="width: 80%">{{ row.status.description }}</Button>
          </div>
          <div v-else-if="status === 'PROCESSING'">
            <Radio v-model="quotationList[index].selectApprove" style="display: inline-block; font-size: 14px;" @on-change="value => changeApprove(value, index)">{{ $t("APPROVE") }}</Radio>
            <Radio v-model="quotationList[index].selectReject" style="display: inline-block; font-size: 14px;" @on-change="value => changeReject(value, index)">{{ $t("REJECT") }}</Radio>
            <Button shape="circle" size="small" style="width: 80%" @click="clearAction(index)">{{ $t("CLEAR") }}</Button>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="comments">
          <Icon v-if="row.resourceType !== 'AbnormalQuotationDto'" type="ios-create" size="28" class="text-delta-blue" @click="showCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="historyComments">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showHistoryCommentsModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="rfqType">
          <span>{{ row.rfqType.description }}</span>
        </template>
        <template slot-scope="{ row, index }" slot="infoRecord">
          <div v-if="row.infoRecordFlag">
            <span>YES</span>
            <Icon type="ios-create" size="28" class="text-delta-blue" @click="showInfoRecordMaintainModal(index, row.uuid)" />
          </div>
          <div v-else>
            <span>NO</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="highPrice">
          <div v-if="row.highPrice && row.quotationHighPrice">
            <Icon type="ios-warning" size="28" class="text-delta-red" />
            <span>{{ row.quotationHighPrice.priceDifference }}%</span>
            <br/>
            <span>{{ row.quotationHighPrice.orgCheck === 'C' ? 'Cross' : 'Single' }}</span>
          </div>
        </template>
        <template slot-scope="{ row, index }" slot="deltaPartAndDeltaPartDescription">
          <div>{{ row.partNo ? row.partNo : "" }}<br>{{ row.partDescription ? row.partDescription : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="vendorCodeAndVendorName">
          <div>{{ row.vendorCode ? row.vendorCode : "" }}<br>{{ row.vendorName ? row.vendorName : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="unitPriceAndCurrency">
          <div v-if="(row.abnormals && row.abnormals.quoteRedAlert) || row.quoteRedAlert">
            <Button type="error" style="width: 80%" shape="circle" size="small">{{ row.unitPrice }}</Button>
            <span>/{{ row.currency }}</span>
          </div>
          <div v-else>{{ row.unitPrice ? row.unitPrice : "" }}/{{ row.currency ? row.currency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="averagePurchasePriceAndCurrency">
          <div>{{ row.averagePurchasePrice ? row.averagePurchasePrice : "" }}/{{ row.averagePurchaseCurrency ? row.averagePurchaseCurrency : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="plmFiles">
          <Icon type="md-download" size="28" class="text-delta-blue" @click="showPlmFilesModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="quotationFiles">
          <Icon v-if="row.isCoverQuote && row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="md-close-circle" size="28" class="text-delta-red" @click="showQuotationFilesModal(index)" />
          <Icon v-else-if="row.isCoverQuote" type="md-close-circle" size="28" class="text-delta-red" />
          <Icon v-else-if="row.supplierQuotationFiles && row.supplierQuotationFiles.filter(attachement => !attachement.deleted).length > 0" type="ios-cloud-download" size="28" class="text-delta-blue" @click="showQuotationFilesModal(index)"/>
        </template>
        <template slot-scope="{ row, index }" slot="referenceFile">
          <Icon :type="row.cpcToSupervisorAttachments && row.cpcToSupervisorAttachments.filter(attachement => !attachement.deleted).length > 0 ? 'ios-cloud-download' : ''" size="28" class="text-delta-blue" @click="showReferenceFileModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="scalePrice">
          <Icon :type="row.quotationScalePriceList && row.quotationScalePriceList.length > 0 ? 'md-reorder' : ''" size="28" class="text-delta-blue" @click="showScalePriceModal(index)" />
        </template>
        <template slot-scope="{ row, index }" slot="referencePriceData">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showReferencePriceDataModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="rawMaterialPriceAndUnit">
          <div>{{ row.rawMaterialPrice ? row.rawMaterialPrice : "" }}/{{ row.rawMaterialUnit ? row.rawMaterialUnit : "" }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="remark">
          <Icon v-if="row.resourceType !== 'AbnormalQuotationDto'" type="ios-create" size="28" class="text-delta-blue" @click="showRemarkModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="ymgrRemark">
          <div>{{ row.ymgrRemark }}</div>
        </template>
        <template slot-scope="{ row, index }" slot="log">
          <Icon type="ios-paper" size="28" class="text-delta-blue" @click="showLogModal(index, row.uuid)" />
        </template>
        <template slot-scope="{ row, index }" slot="categoryAttributes">
          <Icon v-if="row.category && row.category !== 'OTHERS'" type="ios-paper" size="28" class="text-delta-blue" @click="showCategoryAttributesModal(index, row.category, row.formNo)" />
        </template>
        <template slot-scope="{ row, index }" slot="itemCategory">
          <span v-if="quotationList[index].itemCategory === 'STANDARD'">Standard</span>
          <span v-if="quotationList[index].itemCategory === 'SUBCONTRACTION'">Subcontraction</span>
        </template>
        <template slot-scope="{ row, index }" slot="validFromAndValidTo">
          <DatePicker :disabled="status !== 'PROCESSING'" :transfer="true" v-model="quotationList[index].validFrom" type="date" format="yyyy-MM-dd" placeholder="Select Date" @on-change="updateDate($event, index, 'validFrom')"></DatePicker>
          <DatePicker :disabled="status !== 'PROCESSING'" :transfer="true" v-model="quotationList[index].validTo" type="date" format="yyyy-MM-dd" placeholder="Select Date" @on-change="updateDate($event, index, 'validTo')"></DatePicker>
        </template>
      </Table>
      <Row type="flex" justify="space-between">
        <Col type="flex" justify="start">
          {{ $t("COMMON_TEXT_FROM") }} {{ pageObj.page * pageObj.pageSize - (pageObj.pageSize - 1) }} {{ $t("COMMON_TEXT_TO") }}
          <span v-if="pageObj.totalCount < pageObj.page * pageObj.pageSize">{{ pageObj.totalCount }}</span>
          <span v-else> {{ pageObj.page * pageObj.pageSize }}</span>
          {{ $t("COMMON_TEXT_OF") }} {{ pageObj.totalCount }} {{ $t("COMMON_TEXT_ENTRIES") }}
        </Col>
        <Col type="flex" justify="end">
          <Page :total="pageObj.totalCount" :page-size="pageObj.pageSize" :prev-text="$t('COMMON_TEXT_PREV')" :next-text="$t('COMMON_TEXT_NEXT')" @on-change="changePage" @on-prev="changePage(pageObj.page - 1)" @on-next="changePage(pageObj.page + 1)">
          </Page>
        </Col>
      </Row>
      <ButtonRow>
        <Button v-if="status === 'PROCESSING'" type="primary" @click="Submit" slot="1">{{ $t('SUBMIT') }}</Button>
      </ButtonRow>
    </Feature>
    <Feature :title="'Group Data'" :iconClass="'fa-pencil-square-o'">
      <Table :columns="quotationGroupColumnList" :data="quotationGroupList">
        <template slot-scope="{ row, index }" slot="groupKey">
          <a v-if="row.rfqType.rfqTypeEnum === 'COMPONENT'" style="color: #46a3ff;" @click="clickComponentGroupKey(index, row)">{{ row.groupId }}</a>
          <a v-if="row.rfqType.rfqTypeEnum === 'TOOLING'" style="color: #46a3ff;" @click="clickToolingGroupKey(index, row)">{{ row.groupId }}</a>
        </template>
        <template slot-scope="{ row, index }" slot="formNo">
          <span style="white-space: pre-wrap;">{{ row.quotationList.map(q => q.formNo).filter(fn => fn).join("\n") }}</span>
        </template>
        <template slot-scope="{ row, index }" slot="vendorCode">
          <span style="white-space: pre-wrap;">{{ row.quotationList.map(q => q.vendorCode).filter(vc => vc).join("\n") }}</span>
        </template>
        <template slot-scope="{ row, index }" slot="vendorName">
          <span style="white-space: pre-wrap;">{{ row.quotationList.map(q => q.vendorName).filter(vn => vn).join("\n") }}</span>
        </template>
        <template slot-scope="{ row, index }" slot="partNo">
          <span style="white-space: pre-wrap;">{{ row.quotationList.map(q => q.partNo).filter(pn => pn).join("\n") }}</span>
        </template>
      </Table>
      <Row type="flex" justify="space-between">
        <Col type="flex" justify="start">
          {{ $t("COMMON_TEXT_FROM") }} {{ groupPageObj.page * groupPageObj.pageSize - (groupPageObj.pageSize - 1) }} {{ $t("COMMON_TEXT_TO") }}
          <span v-if="groupPageObj.totalCount < groupPageObj.page * groupPageObj.pageSize">{{ groupPageObj.totalCount }}</span>
          <span v-else> {{ groupPageObj.page * groupPageObj.pageSize }}</span>
          {{ $t("COMMON_TEXT_OF") }} {{ groupPageObj.totalCount }} {{ $t("COMMON_TEXT_ENTRIES") }}
        </Col>
        <Col type="flex" justify="end">
          <Page :total="groupPageObj.totalCount" :page-size="groupPageObj.pageSize" :prev-text="$t('COMMON_TEXT_PREV')" :next-text="$t('COMMON_TEXT_NEXT')" @on-change="changeGroupPage">
          </Page>
        </Col>
      </Row>
    </Feature>
    <Feature title="RFQ Data" :iconClass="'fa-pencil-square-o'">
      <Table :columns="quotationRfqColumnList" :data="quotationRfqList">
        <template slot-scope="{ row, index }" slot="groupKey">
          <a style="color: #46a3ff;" @click="clickRfqGroupKey(index, row)">{{ row.groupId }}</a>
        </template>
      </Table>
      <Row type="flex" justify="space-between">
        <Col type="flex" justify="start">
          {{ $t("COMMON_TEXT_FROM") }} {{ rfqPageObj.page * rfqPageObj.pageSize - (rfqPageObj.pageSize - 1) }} {{ $t("COMMON_TEXT_TO") }}
          <span v-if="rfqPageObj.totalCount < rfqPageObj.page * rfqPageObj.pageSize">{{ rfqPageObj.totalCount }}</span>
          <span v-else> {{ rfqPageObj.page * rfqPageObj.pageSize }}</span>
          {{ $t("COMMON_TEXT_OF") }} {{ rfqPageObj.totalCount }} {{ $t("COMMON_TEXT_ENTRIES") }}
        </Col>
        <Col type="flex" justify="end">
          <Page :total="rfqPageObj.totalCount" :page-size="rfqPageObj.pageSize" :prev-text="$t('COMMON_TEXT_PREV')" :next-text="$t('COMMON_TEXT_NEXT')" @on-change="changeRfqPage">
          </Page>
        </Col>
      </Row>
    </Feature>

    <Modal :z-index="modalSize" :reset-drag-position="true" v-model="deltaPnModel" sticky :draggable="true"
      :title="$t('DELTA_PN_MORE')" @on-ok="confirmDeltaPnModel">
      <Input v-model="deltaPnValue" type="textarea" :rows="10" />
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" v-model="vendorCodeModel" sticky :draggable="true"
      :title="$t('VENDOR_PN_MORE')" @on-ok="confirmVendorCodeValue">
      <Input v-model="vendorCodeValue" type="textarea" :rows="10" />
    </Modal>

    <Modal :z-index="modalSize" :reset-drag-position="true" width="900" v-model="logModel" sticky :draggable="true"
      :title="$t('PROCESS_LOG')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <TablePage :columnsList="quotationLogColumnList" :resultList="quotationLogList" :tableMaxHeight="550"></TablePage>
      </div>
    </Modal>:
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="plmFileModel"
      :title="$t('PLM_FILES')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <PLMDocument :plmQuotationDocuments="quotationDocuments" :quotationUuid="quotationUuid"></PLMDocument>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" width="900" v-model="supplierQuotationFilesModel" sticky
      :draggable="true" :title="$t('QUOTATION_FILE')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <FileDocument :attachments="supplierQuotationFiles"></FileDocument>
      </div>
    </Modal>
    <Modal :z-index="999" :reset-drag-position="true" width="900" v-model="cpcToSupervisorAttachmentsModel" sticky
      :draggable="true" :title="$t('REFERENCE_FILE')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <FileDocument :attachments="cpcToSupervisorAttachments"></FileDocument>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" width="900" v-model="scalePricesModel" sticky
      :draggable="true" :title="$t('SCALE_PRICE')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <Table :columns="scalePriceColumnList" :data="scalePrices" :max-height="550"></Table>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" width="900" v-model="commentsModel" sticky :draggable="true"
      :title="$t('COMMENTS')" @on-ok="confirmComments">
      <froala :config="FloraEditorConfig" v-model="comments"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" width="900" v-model="remarkModel" :draggable="true"
      :title="$t('REMARK')" @on-ok="confirmRemark">
      <froala :config="FloraEditorConfig" v-model="remark"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" width="1400" v-model="referencePriceModel" sticky
      :draggable="true" :title="$t('REFERENCE_PRICE_DATA')">
      <div style="max-height:550px; overflow-y:auto; overflow-x:hidden;">
        <Table :columns="referencePriceColumnList" :data="referencePrices" :max-height="550"></Table>
      </div>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900"
      v-model="flowCommentModel" :title="$t('COMMENTS')">
      <froala :config="FloraEditorConfig" v-model="flowComment"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="logCommentModel"
      :title="$t('HISTORY') + ' ' + $t('COMMENTS')">
      <froala :config="FloraEditorConfig" v-model="logComment"></froala>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="1300"
      v-model="metalAttributeModel" :title="$t('METAL_CASE_ATTRIBUTES')">
      <MetalAttributes :metalQuotation="metalQuotation" :referencePrice="referencePrice" :priceList="priceList">
      </MetalAttributes>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="1300"
      v-model="plasticAttributeModel" :title="$t('PLASTIC_CASE_ATTRIBUTES')">
      <PlasticAttributes :plasticQuotation="plasticQuotation" :referencePrice="referencePrice" :priceList="priceList">
      </PlasticAttributes>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="1300"
      v-model="pwbAttributeModel" :title="$t('PWB_CASE_ATTRIBUTES')">
      <PwbAttributes :pwbQuotation="pwbQuotation" :priceList="priceList"></PwbAttributes>
    </Modal>
    <Modal :z-index="modalSize" :reset-drag-position="true" sticky :draggable="true" width="900" v-model="infoRecordMaintainModal" :title="$t('INFO_RECORD_MAINTAIN')">
      <InfoRecordMaintain v-if="infoRecordMaintainModal" :quotation="quotation" :materialTypeList="materialTypeList" :readonly="true"></InfoRecordMaintain>
    </Modal>
  </section>
</template>

<script>
import { mapGetters } from "vuex"
import { mapActions } from "vuex"
import PLMDocument from '../../components/PLMDocument.vue'
import FileDocument from '../../components/FileDocument.vue'
import { AlertInfo, AlertError } from "@/util/alert"
import { Translate, FloraEditorConfig } from "@/util/common"
import moment from "moment"
import MetalAttributes from "@/views/quotation/MetalAttributes"
import PlasticAttributes from "@/views/quotation/PlasticAttributes"
import PwbAttributes from "@/views/quotation/PwbAttributes"
import InfoRecordMaintain from "@/views/quotation/InfoRecordMaintain"

export default {
  name: "SupervisorMaintain",
  components: {
    PLMDocument,
    FileDocument,
    MetalAttributes,
    PlasticAttributes,
    PwbAttributes,
    InfoRecordMaintain
  },
  inject: ['reload'],
  created() {
    const vueInstance = this;
    this.defaultValidTo = getDefaultValidTo();
    vueInstance.$queryStatusList(vueInstance, "supervisor")
      .then(data => {
        if (data) {
          vueInstance.statusList = data;
          if (!vueInstance.status) {
            vueInstance.status = vueInstance.statusList[0].statusViewEnum;
          }
          vueInstance.search(true);
        } else {
          AlertInfo(vueInstance, vueInstance.$t('COMMON_MESSAGE_NOT_AUTH'))
            .then(() => {});
          vueInstance.$router.push("/home");
        }
      });
    vueInstance.$quotationHttp
      .get("/quotation/material-type")
      .then(response => {
        if (response.data) {
          vueInstance.materialTypeList = response.data;
        }
      });
  },
  data() {
    return {
      FloraEditorConfig: FloraEditorConfig(""),
      actionType: '',
      selectAllType: '',
      layoutType: "DEFAULT",
      pageObj: {
        "page": 0,
        "totalPage": 0,
        "totalCount": 0,
        "pageSize": 20,
        "sort": ["partNo,asc"]
      },
      groupPageObj: {
        "page": 0,
        "totalPage": 0,
        "totalCount": 0,
        "pageSize": 20
      },
      rfqPageObj: {
        "page": 0,
        "totalPage": 0,
        "totalCount": 0,
        "pageSize": 20
      },
      defaultValidTo: '',
      statusList: [],
      deltaPnValue: '',
      vendorCodeValue: '',
      deltaPnModel: false,
      vendorCodeModel: false,
      quotationUuid: '',
      logModel: false,
      plmFileModel: false,
      supplierQuotationFilesModel: false,
      cpcToSupervisorAttachmentsModel: false,
      cpcToSupervisorAttachments: [],
      scalePricesModel: false,
      commentsModel: false,
      remarkModel: false,
      referencePriceModel: false,
      flowCommentModel: false,
      flowComment: '',
      logComment: '',
      logCommentModel: false,
      quotationDocuments: [],
      quotationLogList: [],
      supplierQuotationFiles: [],
      scalePrices: [],
      comments: '',
      remark: '',
      excelQuotationList: [],
      metalAttributeModel: false,
      metalQuotation: {
        length: '',
        width: '',
        height: '',
        weight: '',
        rawMaterialType: {
          partNo: '',
          partNoDesc: ''
        },
        rmVendor: {
          vendorCode: ''
        },
        coilCenter: {
          vendorCode: ''
        },
        rmPrice: '',
        rmPriceDate: '',
        rmCost: '',
        processingCost: '',
        treatmentCost: '',
        accessoriesCost: '',
        others: '',
        rmRatio: '',
        totalCost: '',
        supplierRemark: ''
      },
      plasticAttributeModel: false,
      plasticQuotation: {
        length: '',
        width: '',
        height: '',
        weight: '',
        model: '',
        resinType: {
          type: ''
        },
        resinVendor: {
          vendorCode: ''
        },
        resinNo: {
          partNo: '',
          partNoDesc: ''
        },
        distributor: {
          vendorCode: ''
        },
        resinColor: '',
        colorNo: '',
        rmPrice: '',
        rmPriceDate: '',
        dyeCost: '',
        cavity: '',
        tannage: '',
        hourlyRate: '',
        cycleTime: '',
        rmCost: '',
        injectingCost: '',
        processingCost: '',
        treatmentCost: '',
        accessoriesCost: '',
        others: '',
        rmRatio: '',
        totalCost: '',
        supplierRemark: ''
      },
      pwbAttributeModel: false,
      pwbQuotation: {
        deltaExchangeRate: "",
        cpcExchangeRate: "",
        rfqBase: "",
        cclLaminateType: "",
        layer: "",
        pcbThickness: "",
        internalCoreThickness: "",
        tg: "",
        externalCopperThickness: "",
        internalCopperThickness: "",
        mainSourceCclSupplier: {
          vendorCode: ""
        },
        mainSourceCclShare: 0,
        mainCoreUlNumber: {
          partNoDesc: ""
        },
        mainCoreUP: "",
        secondSourceCclSupplier: {
          vendorCode: ""
        },
        secondCoreUlNumber: {
          partNoDesc: ""
        },
        secondCoreUP: "",
        pcbPrice: "",
        panelLength: "",
        panelWidth: "",
        pnLength: "",
        pnWidth: "",
        area: "",
        pnArea: "",
        supplierSheetSize: "",
        deltaSheetSize: "",
        panelSheet: "",
        pnInSheet: "",
        pnInPanel: "",
        pnUtilization: "",
        panelUtilization: "",
        combineRatio: "",
        numberOfDrill: "",
        slotLength: "",
        goldArea: "",
        materialCost: "",
        surfaceFinishCost: "",
        processCost: "",
        specialCostDown: "",
        deltaPnPriceUsd: "",
        deltaPnPrice: "",
        supplierRemark: ""
      },
      referencePrice: '',
      priceList: [],
      referencePriceColumnList: [
        {
          renderHeader: (h, params) => {
            var text = this.$t('MATERIAL');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 140,
          key: "materialNumber",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('VENDOR_CODE') + "<br>" + this.$t('OA_VENDOR_NAME')
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 140,
          render: (h, params) => {
            let vendorCode = params.row.vendorCode != undefined ? params.row.vendorCode : ""
            let vendorName = params.row.vendorName != undefined ? params.row.vendorName : ""
            let text = vendorCode + "<br>" + vendorName
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          }
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('AGENT');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "agent",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('AGENT_NAME');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 140,
          key: "agentName",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('INFO_CATEGORY');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "purchInfoRecordCategory",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('VALID_FROM');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "validFromDate",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('VALID_TO');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "validToDate",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('PLANT_CODE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 100,
          key: "plant",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('CURRENCY');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "currency",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('UNIT_PRICE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "unitPirce",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('USD_PRICE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "usdPrice",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('UOM');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "conditionUnit",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('%');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          width: 80,
          key: "percentage",
        },
      ],
      referencePrices: [],
      quotationGroupList: [],
      quotationRfqList: [],
      scalePriceColumnList: [
        {
          title: 'Item',
          key: 'item',
          align: 'center',
          width: 80
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('SCALE') + '<br>' + this.$t('QUANTITY')
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          key: 'scaleQuantity',
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('SCALE') + '<br>' + this.$t('PRICE')
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          align: 'center',
          key: 'scalePrice'
        }
      ],
      quotationLogColumnList: [
        {
          renderHeader: (h, params) => {
            var text = this.$t('ROLE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "quotationRole",
          width: 150,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('STATUS');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "flowStatus",
          width: 150,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('GROUP_ACCOUNT');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "groupAccount",
          width: 180,
          align: "center",
          render: (h, params) => {
            return h("span", {}, params.row["groupAccount"].code + '/' + params.row["groupAccount"].companyName)
          }
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('ACTION');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "flowAction",
          width: 150,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('COMMENTS');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          width: 150,
          align: "center",
          render: (h, params) => {
            if (params.row.flowComment) {
              return h("Icon", {
                props: {
                  type: "ios-paper",
                  size: 28,
                },
                class: "text-delta-blue",
                on: {
                  click: () => {
                    this.flowComment = params.row.flowComment
                    this.flowCommentModel = true
                  },
                },
              })
            } else {
              return h("span", {}, "")
            }
          }
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('CREATE_DATE');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "created",
          width: 180,
          align: "center",
        },
        {
          renderHeader: (h, params) => {
            var text = this.$t('PERSON_ACCOUNT');
            return h('div', {
              domProps: {
                innerHTML: text
              }
            })
          },
          key: "personEmail",
          width: 200,
          align: "center",
        },
      ],
      quotationList: [],
      deltaPn: "",
      vendorCode: "",
      fromRfqDate: "",
      toRfqDate: "",
      status: "",
      quotation: {}, 
      infoRecordMaintainModal: false,
      materialTypeList: []
    }
  },
  methods: {
    ...mapGetters(["getUserProfile", "getLanguage", "getModalSize", "getWindowsWidth", "getSupervisorStatus"]),
    ...mapActions(["toggleLoading", 'setSupervisorGroupId', 'setSupervisorGroupUuid', "setSupervisorStatus"]),
    changeSelectAll() {
      this.metalAttributeModel = false
      this.plasticAttributeModel = false
      if (this.selectAllType === 'APPROVE') {
        this.quotationList.forEach(quotation => {
          this.$set(quotation, "selectApprove", true);
          this.$set(quotation, "selectReject", false);
          this.$set(quotation, "action", "APPROVE");
        });
      } else if (this.selectAllType === 'REJECT') {
        this.quotationList.forEach(quotation => {
          this.$set(quotation, "selectApprove", false);
          this.$set(quotation, "selectReject", true);
          this.$set(quotation, "action", "REJECT");
        });
      }
    },
    changeApprove(value, index) {
      if (value === true) {
        this.quotationList[index].selectApprove = true;
        this.quotationList[index].selectReject = false;
        this.quotationList[index].action = "APPROVE";
      }
    },
    changeReject(value, index) {
      if (value === true) {
        this.quotationList[index].selectApprove = false;
        this.quotationList[index].selectReject = true;
        this.quotationList[index].action = "REJECT";
      }
    },
    updateDate(value, index, attr) {
      this.quotationList[index][attr] = value;
    },
    cleanSelect() {
      this.metalAttributeModel = false;
      this.plasticAttributeModel = false;
      this.selectAllType = "";
      this.quotationList.forEach(quotation => {
        this.$set(quotation, "selectApprove", false);
        this.$set(quotation, "selectReject", false);
        this.$set(quotation, "action", "");
      });
    },
    moreDeltaPn() {
      this.deltaPnModel = true
      this.deltaPn = "";
    },
    moreVendorCode() {
      this.vendorCodeModel = true
      this.vendorCode = "";
    },
    confirmDeltaPnModel() {
      this.deltaPn = this.deltaPnValue.split('\n').join(";");
    },
    confirmVendorCodeValue() {
      this.vendorCode = this.vendorCodeValue.split('\n').join(";");
    },
    changeStatus() {
      this.quotationList = [];
      this.quotationGroupList = [];
    },
    changePage(page) {
      const vueInstance = this;
      vueInstance.pageObj.page = page - 1
      vueInstance.toggleLoading(true);
      vueInstance.searchSingle(false)
        .then(result => {
          if (result === undefined) {
            return;
          }
          vueInstance.pageObj = {
            "page": result.number + 1,
            "totalPage": result.totalPages,
            "totalCount": result.totalElements,
            "pageSize": result.size,
            "sort": ["partNo,asc"]
          };
          vueInstance.quotationList = result.content;
          vueInstance.quotationList.forEach(quotation => {
            vueInstance.$queryAbnormals(vueInstance, quotation.uuid)
              .then(data => {
                if (data) {
                  data.forEach(abnormalQuotation => {
                    abnormalQuotation.key = quotation.uuid + "_" + abnormalQuotation.uuid
                  });
                  quotation.key = quotation.uuid;
                  quotation.children = data;
                  quotation._showChildren = true;
                  let index = vueInstance.quotationList.findIndex((temp) => temp.uuid === quotation.uuid);
                  vueInstance.$set(vueInstance.quotationList, index, quotation);
                }
              });
          });
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    },
    changeGroupPage(page) {
      const vueInstance = this;
      vueInstance.groupPageObj.page = page - 1
      vueInstance.toggleLoading(true);
      vueInstance.searchGroup(false)
        .then(result => {
          if (result === undefined) {
            return;
          }
          vueInstance.groupPageObj = {
            "page": result.number + 1,
            "totalPage": result.totalPages,
            "totalCount": result.totalElements,
            "pageSize": result.size
          };
          vueInstance.quotationGroupList = result.content;
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    },
    changeRfqPage(page) {
      const vueInstance = this;
      vueInstance.rfqPageObj.page = page - 1
      vueInstance.toggleLoading(true);
      vueInstance.searchRfq(false)
        .then(result => {
          if (result === undefined) {
            return;
          }
          vueInstance.rfqPageObj = {
            "page": result.number + 1,
            "totalPage": result.totalPages,
            "totalCount": result.totalElements,
            "pageSize": result.size
          };
          vueInstance.quotationRfqList = result.content;
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    },
    search(init) {
      const vueInstance = this;
      vueInstance.toggleLoading(true);
      Promise.all([vueInstance.searchSingle(init), vueInstance.searchGroup(init), vueInstance.searchRfq(init)])
        .then(resultList => {
          if (resultList.every(result => result === undefined)) {
            vueInstance.$Notice.success({
              title: Translate("COMMON_MESSAGE_INFO"),
              desc: Translate("COMMON_MESSAGE_INFO"),
              render: h => h("span", [Translate("COMMON_TEXT_NOTHING")])
            });
            return;
          }
          let singleResult = resultList[0];
          if (singleResult !== undefined) {
            vueInstance.pageObj = {
              "page": singleResult.number + 1,
              "totalPage": singleResult.totalPages,
              "totalCount": singleResult.totalElements,
              "pageSize": singleResult.size,
              "sort": ["partNo,asc"]
            };
            vueInstance.quotationList = singleResult.content;
            vueInstance.quotationList.forEach(quotation => {
              if (quotation.itemCategory === null || quotation.itemCategory === undefined) {
                quotation.itemCategory = "STANDARD";
              }
              if (quotation.validFrom === null || quotation.validFrom === undefined) {
                quotation.validFrom = moment().format("YYYY-MM-DD");
              }
              if (quotation.validTo === null || quotation.validTo === undefined) {
                quotation.validTo = vueInstance.defaultValidTo;
              }
              vueInstance.$queryAbnormals(vueInstance, quotation.uuid)
                .then(data => {
                  if (data) {
                    data.forEach(abnormalQuotation => {
                      abnormalQuotation.key = quotation.uuid + "_" + abnormalQuotation.uuid
                    });
                    quotation.key = quotation.uuid;
                    quotation.children = data;
                    quotation._showChildren = true;
                    let index = vueInstance.quotationList.findIndex((temp) => temp.uuid === quotation.uuid);
                    vueInstance.$set(vueInstance.quotationList, index, quotation);
                  }
                });
            });
          }
          let groupResult = resultList[1];
          if (groupResult !== undefined) {
            vueInstance.groupPageObj = {
              "page": groupResult.number + 1,
              "totalPage": groupResult.totalPages,
              "totalCount": groupResult.totalElements,
              "pageSize": groupResult.size
            };
            vueInstance.quotationGroupList = groupResult.content;
          }
          let rfqResult = resultList[2];
          if (rfqResult !== undefined) {
            vueInstance.rfqPageObj = {
              "page": rfqResult.number + 1,
              "totalPage": rfqResult.totalPages,
              "totalCount": rfqResult.totalElements,
              "pageSize": rfqResult.size
            };
            vueInstance.quotationRfqList = rfqResult.content;
          }
        })
        .catch(error => {
          console.error(error);
          AlertError(vueInstance, error)
            .then(() => {});
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    },
    searchSingle(init) {
      const vueInstance = this;
      vueInstance.selectAllType = "";
      if (init) {
        vueInstance.pageObj = {
          "page": 0,
          "totalPage": 0,
          "totalCount": 0,
          "pageSize": 20,
          "sort": ["partNo,asc"]
        };
      }
      let queryData = {
        "deltaPartNos": vueInstance.deltaPn === "" ? null : vueInstance.deltaPn.split(";"),
        "vendorCodes": vueInstance.vendorCode === "" ? null : vueInstance.vendorCode.split(";"),
        "fromRfqDate": vueInstance.fromRfqDate === "" ? null : moment(vueInstance.fromRfqDate).format('YYYY-MM-DD'),
        "toRfqDate": vueInstance.toRfqDate === "" ? null : moment(vueInstance.toRfqDate).format('YYYY-MM-DD'),
        "status": vueInstance.status
      }
      vueInstance.quotationList = [];
      return vueInstance.$quotationHttp
        .post("/supervisor/search?page=" + vueInstance.pageObj.page + "&size=" + vueInstance.pageObj.pageSize + (vueInstance.pageObj.sort === undefined || Array.isArray(vueInstance.pageObj.sort) === false || vueInstance.pageObj.sort.length === 0 ? "" : "&sort=" + vueInstance.pageObj.sort.join("&sort=")), queryData)
        .then(response => {
          if (response.data) {
            return response.data;
          }
        });
    },
    searchGroup(init) {
      const vueInstance = this;
      if (init) {
        vueInstance.groupPageObj = {
          "page": 0,
          "totalPage": 0,
          "totalCount": 0,
          "pageSize": 20
        };
      }
      let queryData = {
        "deltaPartNos": vueInstance.deltaPn === "" ? [] : vueInstance.deltaPn.split(";"),
        "vendorCodes": vueInstance.vendorCode === "" ? [] : vueInstance.vendorCode.split(";"),
        "fromRfqDate": vueInstance.fromRfqDate === "" ? null : moment(vueInstance.fromRfqDate).format('YYYY-MM-DD'),
        "toRfqDate": vueInstance.toRfqDate === "" ? null : moment(vueInstance.toRfqDate).format('YYYY-MM-DD'),
        "status": vueInstance.status
      }
      vueInstance.quotationGroupList = [];
      return vueInstance.$quotationHttp
        .post("/supervisor/group/search?page=" + vueInstance.groupPageObj.page + "&size=" + vueInstance.groupPageObj.pageSize, queryData)
        .then(response => {
          if (response.data) {
            return response.data;
          }
        });
    },
    searchRfq(init) {
      const vueInstance = this;
      if (init) {
        vueInstance.rfqPageObj = {
          "page": 0,
          "totalPage": 0,
          "totalCount": 0,
          "pageSize": 20
        };
      }
      let queryData = {
        "deltaPartNos": vueInstance.deltaPn === "" ? [] : vueInstance.deltaPn.split(";"),
        "vendorCodes": vueInstance.vendorCode === "" ? [] : vueInstance.vendorCode.split(";"),
        "fromRfqDate": vueInstance.fromRfqDate === "" ? null : moment(vueInstance.fromRfqDate).format('YYYY-MM-DD'),
        "toRfqDate": vueInstance.toRfqDate === "" ? null : moment(vueInstance.toRfqDate).format('YYYY-MM-DD'),
        "status": vueInstance.status
      }
      vueInstance.quotationRfqList = [];
      return vueInstance.$quotationHttp
        .post("/supervisor/rfq/search?page=" + vueInstance.rfqPageObj.page + "&size=" + vueInstance.rfqPageObj.pageSize, queryData)
        .then(response => {
          if (response.data) {
            return response.data;
          }
        });
    },
    clear() {
      this.deltaPn = "";
      this.vendorCode = "";
      this.fromRfqDate = "";
      this.toRfqDate = "";
      this.deltaPnValue = ''
      this.vendorCodeValue = ''
      this.status = "PROCESSING";
    },
    confirmComments() {
      const vueInstance = this
      vueInstance.quotationList.forEach(temp => {
        if (temp.uuid == vueInstance.quotationUuid) {
          temp.comments = vueInstance.comments
        } else if (vueInstance.actionType && vueInstance.actionType == temp.action) {
          temp.comments = vueInstance.comments
        }
      })
    },
    confirmRemark() {
      this.quotationList.forEach(temp => {
        if (temp.id == this.quotationUuid) {
          temp.remark = this.remark
        }
      })
    },
    Submit() {
      const vueInstance = this;
      vueInstance.toggleLoading(true);
      let quotationRequestListForApprove = [];
      let quotationRequestListForReject = [];
      vueInstance.quotationList.forEach(quotation => {
        switch (quotation.action) {
          case "APPROVE":
            quotationRequestListForApprove.push({
              uuid: quotation.uuid,
              comments: quotation.comments,
              validFrom: quotation.validFrom,
              validTo: quotation.validTo
            });
            break;
          case "REJECT":
            quotationRequestListForReject.push({
              uuid: quotation.uuid,
              comments: quotation.comments
            });
            break;
        }
      });
      vueInstance.$quotationHttp
        .post("/supervisor/submit", {
          quotationRequestListForApprove: quotationRequestListForApprove,
          quotationRequestListForReject: quotationRequestListForReject
        })
        .then(response => {
          if (response.data) {
            vueInstance.$Swal.fire({
              icon: "info",
              title: vueInstance.$t("COMMON_MESSAGE_INFO"),
              html: response.data.content[0].status,
            })
            .then(() => {
              vueInstance.reload();
            });
          }
        })
        .catch(error => {
          console.error(error);
          vueInstance.$Swal.fire({
            icon: "error",
            title: Translate("COMMON_MESSAGE_ERROR"),
            text: vueInstance.$buildErrorMessage(error)
          });
        })
        .finally(() => {
          vueInstance.toggleLoading(false);
        });
    },
    clearAction(index) {
      this.quotationList[index].selectApprove = false;
      this.quotationList[index].selectReject = false;
      this.quotationList[index].action = "";
    },
    showCommentsModal(index, uuid) {
      this.comments = this.quotationList[index].comments ?? "";
      this.actionType = this.quotationList[index].action ?? "";
      this.quotationUuid = uuid;
      this.commentsModel = true;
    },
    showHistoryCommentsModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryQuotationLog(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.logComment = "";
          data.forEach(log => {
            if (log.quotationRole) {
              this.logComment = this.logComment + this.$t("ROLE") + ": " + log.quotationRole + ", ";
            }
            if (log.quotationRole) {
              this.logComment = this.logComment + this.$t("STATUS") + ": " + log.flowStatus + ", ";
            }
            if (log.groupAccount) {
              this.logComment = this.logComment + this.$t("GROUP_ACCOUNT") + ": " + log.groupAccount.code + "/" + log.groupAccount.companyName + ", ";
            }
            if (log.created) {
              this.logComment = this.logComment + this.$t("CREATE_DATE") + ": " + log.created + ": <br>";
            }
            if (log.flowComment) {
              this.logComment = this.logComment + log.flowComment;
            }
            this.logComment = this.logComment + "<hr style=\"border:1px dashed #000; height:1px\">";
          });
          this.logCommentModel = true;
        })
        .catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showInfoRecordMaintainModal(index, uuid) {
      this.quotation = this.quotationList[index].quotationDetailInfoRecordDto;
      this.infoRecordMaintainModal = true;
    },
    showPlmFilesModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryPlmDocuments(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.quotationDocuments = data;
          this.plmFileModel = true;
        }).catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showQuotationFilesModal(index) {
      this.supplierQuotationFiles = this.quotationList[index].supplierQuotationFiles.filter(attachment => !attachment.deleted);
      this.supplierQuotationFilesModel = true;
    },
    showReferenceFileModal(index) {
      this.cpcToSupervisorAttachments = this.quotationList[index].cpcToSupervisorAttachments.filter(attachment => !attachment.deleted);
      this.cpcToSupervisorAttachmentsModel = true;
    },
    showScalePriceModal(index) {
      this.scalePrices = this.quotationList[index].quotationScalePriceList;
      this.scalePricesModel = true;
    },
    showReferencePriceDataModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryReferencePrice(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.referencePrices = data;
          this.referencePriceModel = true;
        }).catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showRemarkModal(index, uuid) {
      this.remark = this.quotationList[index].remark;
      this.quotationUuid = uuid;
      this.remarkModel = true;
    },
    showLogModal(index, uuid) {
      this.toggleLoading(true);
      this.$queryQuotationLog(this, uuid)
        .then(data => {
          this.quotationUuid = uuid;
          this.quotationLogList = data;
          this.logModel = true;
        }).catch(error => {
          AlertInfo(this, this.$t("COMMON_MESSAGE_SYSTEM_ERROR"))
            .then(() => {});
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    showCategoryAttributesModal(index, category, formNo) {
      switch (category) {
        case "METAL":
          this.$showMetalCategoryAttribute(this, formNo);
          break;
        case "PLASTIC":
          this.$showPlasticCategoryAttribute(this, formNo);
          break;
        case "PWB":
          this.$showPwbCategoryAttribute(this, formNo);
          break;
      }
    },
    clickComponentGroupKey(index, row) {
      const vueInstance = this;
      vueInstance.setSupervisorGroupId(row.groupId);
      vueInstance.setSupervisorGroupUuid(row.uuid);
      vueInstance.$router.push({
        name: "SUPERVISOR_MAINTAIN_DETAIL_GROUP_ID",
        params: {
          status: vueInstance.status
        }
      });
    },
    clickToolingGroupKey(index, row) {
      const vueInstance = this;
      vueInstance.setSupervisorGroupId(row.groupId);
      vueInstance.setSupervisorGroupUuid(row.uuid);
      vueInstance.$router.push({
        name: "SUPERVISOR_MAINTAIN_DETAIL_TOOLING_GROUP_ID",
        params: {
          quotationStatus: vueInstance.status
        }
      });
    },
    clickRfqGroupKey(index, row) {
      const vueInstance = this;
      vueInstance.setSupervisorGroupId(row.groupId);
      vueInstance.setSupervisorGroupUuid(row.uuid);
      vueInstance.$router.push({
        name: "SUPERVISOR_MAINTAIN_DETAIL_RFQ_GROUP_ID",
        params: {
          quotationStatus: vueInstance.status
        }
      });
    }
  },
  computed: {
    quotationView() {
      return this.quotationList && this.quotationList.length > 0;
    },
    quotationGroupView() {
      return this.quotationGroupList && this.quotationGroupList.length > 0;
    },
    quotationRfqView() {
      return this.quotationRfqList && this.quotationRfqList.length > 0;
    },
    modalSize() {
      return this.getModalSize()
    },
    windowWidth() {
      return this.getWindowsWidth() - 50
    },
    quotationColumnListForSimple() {
      return [{
        title: "#",
        align: "center",
        width: 40,
        tree: true,
      }, {
        title: this.$t("CLEAR"),
        align: "center",
        width: 80,
        slot: "clear"
      }, {
        title: this.$t("APPROVE"),
        align: "center",
        width: 80,
        slot: "approve"
      }, {
        title: this.$t("REJECT"),
        align: "center",
        width: 80,
        slot: "reject"
      }, {
        title: this.$t("COMMENTS"),
        align: "center",
        width: 90,
        slot: "comments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("HISTORY"), 
            h("br"), 
            this.$t("COMMENTS")
          ]);
        },
        align: "center",
        width: 70,
        slot: "historyComments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("QUOTATION"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "quotationFiles"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE"), 
            h("br"), 
            this.$t("FILE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referenceFile"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PLM"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "plmFiles"
      }, {
        title: this.$t("VALID_FROM"),
        align: "center",
        width: 120,
        slot: "validFrom"
      }, {
        title: this.$t("VALID_TO"),
        align: "center",
        width: 120,
        slot: "validTo"
      }, {
        title: this.$t("HIGH_PRICE"),
        align: "center",
        width: 60,
        slot: "highPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("TARGET"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 80,
        key: "targetPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("UNIT_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "unitPriceAndCurrency"
      }, {
        title: this.$t("DELTA_PN"),
        align: "center",
        width: 100,
        key: "partNo"
      }, {
        title: this.$t("OA_VENDOR_NAME"),
        align: "center",
        width: 90,
        key: "vendorName"
      }, {
        title: this.$t("VENDOR_PN"),
        align: "center",
        width: 80,
        key: "vendorPartNo"
      }, {
        title: this.$t("PLANT_CODE"),
        align: "center",
        width: 50,
        key: "plant"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE_PRICE"), 
            h("br"), 
            this.$t("DATA")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referencePriceData"
      }, {
        title: this.$t("ITEM_CATEGORY"),
        align: "center",
        width: 100,
        slot: "itemCategory"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PROCESS_DAYS"), 
            h("br"), 
            this.$t("CPC")
          ]);
        },
        align: "center",
        width: 80,
        key: "processDays"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("AVERAGE_PURCHASE_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "averagePurchasePriceAndCurrency"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("SCALE"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "scalePrice"
      }, {
        title: this.$t("RAW_MATERIAL_TYPE"),
        align: "center",
        width: 80,
        key: "rawMaterialType"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("RAW_MATERIAL"), 
            h("br"), 
            this.$t("PRICE_UNIT")
          ]);
        },
        align: "center",
        width: 80,
        slot: "rawMaterialPriceAndUnit"
      }, {
        title: this.$t("RM_REMARK"),
        align: "center",
        width: 60,
        slot: "remark"
      }, {
        title: this.$t("YMGR_REMARK"),
        align: "center",
        width: 60,
        slot: "ymgrRemark"
      }, {
        title: this.$t("LOG"),
        align: "center",
        width: 60,
        slot: "log"
      }, {
        title: this.$t("CATEGORY_ATTRIBUTES"),
        align: "center",
        width: 50,
        slot: "categoryAttributes"
      }, {
        title: this.$t("INFO_RECORD"),
        align: "center",
        width: 60,
        slot: "infoRecord"
      }, {
        title: this.$t("RFQ_TYPE"),
        align: "center",
        width: 85,
        slot: "rfqType"
      }, {
        title: this.$t("CATEGORY"),
        align: "center",
        width: 60,
        key: "category"
      }];
    },
    quotationColumnList() {
      return [{
        title: "#",
        align: "center",
        width: 40,
        tree: true,
      }, {
        title: this.$t("ACTION"),
        align: "center",
        width: 80,
        slot: "action"
      }, {
        title: this.$t("COMMENTS"),
        align: "center",
        width: 90,
        slot: "comments"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("HISTORY"), 
            h("br"), 
            this.$t("COMMENTS")
          ]);
        },
        align: "center",
        width: 70,
        slot: "historyComments"
      },{
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PROCESS_DAYS"), 
            h("br"), 
            this.$t("CPC")
          ]);
        },
        align: "center",
        width: 80,
        key: "processDays"
      },{
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("VALID_FROM"), 
            h("br"), 
            this.$t("VALID_TO")
          ]);
        },
        align: "center",
        width: 140,
        slot: "validFromAndValidTo"
      }, {
        title: this.$t("HIGH_PRICE"),
        align: "center",
        width: 60,
        slot: "highPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("TARGET"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 80,
        key: "targetPrice"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("UNIT_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "unitPriceAndCurrency"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE_PRICE"), 
            h("br"), 
            this.$t("DATA")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referencePriceData"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("QUOTATION"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "quotationFiles"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("PLM"), 
            h("br"), 
            this.$t("FILES")
          ]);
        },
        align: "center",
        width: 60,
        slot: "plmFiles"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("DELTA_PN"), 
            h("br"), 
            this.$t("PN_DESCRIPTION")
          ]);
        },
        align: "center",
        width: 100,
        slot: "deltaPartAndDeltaPartDescription"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("VENDOR_CODE"), 
            h("br"), 
            this.$t("OA_VENDOR_NAME")
          ]);
        },
        align: "center",
        width: 90,
        slot: "vendorCodeAndVendorName"
      }, {
        title: this.$t("VENDOR_PN"),
        align: "center",
        width: 80,
        key: "vendorPartNo"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("AVERAGE_PURCHASE_PRICE"), 
            h("br"), 
            this.$t("CURRENCY")
          ]);
        },
        align: "center",
        width: 90,
        slot: "averagePurchasePriceAndCurrency"
      }, {
        title: this.$t("PLANT_CODE"),
        align: "center",
        width: 50,
        key: "plant"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("REFERENCE"), 
            h("br"), 
            this.$t("FILE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "referenceFile"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("SCALE"), 
            h("br"), 
            this.$t("PRICE")
          ]);
        },
        align: "center",
        width: 60,
        slot: "scalePrice"
      }, {
        title: this.$t("RAW_MATERIAL_TYPE"),
        align: "center",
        width: 80,
        key: "rawMaterialType"
      }, {
        renderHeader: (h, params) => {
          return h("div", {}, [
            this.$t("RAW_MATERIAL"), 
            h("br"), 
            this.$t("PRICE_UNIT")
          ]);
        },
        align: "center",
        width: 80,
        slot: "rawMaterialPriceAndUnit"
      }, {
        title: this.$t("RM_REMARK"),
        align: "center",
        width: 60,
        slot: "remark"
      }, {
        title: this.$t("YMGR_REMARK"),
        align: "center",
        width: 60,
        slot: "ymgrRemark"
      }, {
        title: this.$t("LOG"),
        align: "center",
        width: 60,
        slot: "log"
      }, {
        title: this.$t("CATEGORY_ATTRIBUTES"),
        align: "center",
        width: 50,
        slot: "categoryAttributes"
      }, {
        title: this.$t("ITEM_CATEGORY"),
        align: "center",
        width: 100,
        slot: "itemCategory"
      }, {
        title: this.$t("INFO_RECORD"),
        align: "center",
        width: 60,
        slot: "infoRecord"
      }, {
        title: this.$t("RFQ_TYPE"),
        align: "center",
        width: 85,
        slot: "rfqType"
      }, {
        title: this.$t("CATEGORY"),
        align: "center",
        width: 60,
        key: "category"
      }];
    },
    quotationGroupColumnList() {
      return [{
        title: this.$t("GROUP_KEY"),
        align: "center",
        width: 150,
        slot: "groupKey"
      }, {
        title: this.$t("FORM_NO"),
        align: "center",
        width: 150,
        slot: "formNo"
      }, {
        title: this.$t("VENDOR_CODE"),
        align: "center",
        width: 150,
        slot: "vendorCode"
      }, {
        title: this.$t("OA_VENDOR_NAME"),
        align: "center",
        width: 250,
        slot: "vendorName"
      }, {
        title: this.$t("PART_NO"),
        align: "center",
        width: 150,
        slot: "partNo"
      }];
    },
    quotationRfqColumnList() {
      return [{
        title: this.$t("GROUP_KEY"),
        align: "center",
        width: 150,
        slot: "groupKey"
      }, {
        title: this.$t("VENDOR_CODE"),
        align: "center",
        width: 150,
        key: "vendorCode"
      }, {
        title: this.$t("OA_VENDOR_NAME"),
        align: "center",
        width: 250,
        key: "vendorName"
      }];
    }
  }
}

function getDefaultValidTo() {
  let today = new Date();
  if ((today.getMonth() + 1) > 9) {
    return moment([today.getFullYear() + 1, 11, 31]).format("YYYY-MM-DD")
  } else {
    return moment([today.getFullYear(), 11, 31]).format("YYYY-MM-DD")
  }
}
</script>

<style scoped></style>
